import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState, useCallback } from "react";
import { QrCode } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import qrCode from "../../resources/icons/qr-code.png";
import { POST, GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import Toast from "../Toast/Toast";

const QrEditModal = ({
  isOpen,
  onClose,
  qrPosition,
  setQrPosition,
  qrSize,
  setQrSize,
  certificateRef,
  pdfUrl,
  issuer_credential_template_id,
}) => {
  const [certificateDimensions, setCertificateDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [pdfDimensionsPt, setPdfDimensionsPt] = useState({
    width: 0,
    height: 0,
    rotation: 0,
  });
  const [showVerificationText, setShowVerificationText] = useState(true);
  const [qrCoordsPt, setQrCoordsPt] = useState({
    x: 0,
    y: 0,
    width: 110,
    height: 110,
    is_active: true,
  });
  const [verifierCoordsPt, setVerifierCoordsPt] = useState({
    x: 0,
    y: 0,
    fontSize: 7,
    width: 100,
    height: 20,
    is_active: true,
  });
  const [editableVerifierText, setEditableVerifierText] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragTarget, setDragTarget] = useState(null);
  const [resizing, setResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    fontSize: 0,
    elementX: 0,
    elementY: 0,
  });
  const [activeTab, setActiveTab] = useState("QR");
  const [showResizeDots, setShowResizeDots] = useState(true);

  useEffect(() => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
  }, [])

  const adjustCoordinates = (x, y, w, h, width, height, rotation) => {
    if (rotation === 90) {
      return { x: y, y: width - x - w, w: h, h: w };
    } else if (rotation === 180) {
      return { x: width - x - w, y: height - y - h, w, h };
    } else if (rotation === 270) {
      return { x: height - y - h, y: x, w: h, h: w };
    }
    return { x, y, w, h };
  };

  const loadPdf = async () => {
    try {
      if (!pdfUrl) {
        console.error("No PDF URL provided");
        return;
      }

      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;

      const page = await pdf.getPage(1);

      const viewport = page.getViewport({ scale: 1 });
      const pdfWidthPt = viewport.width;
      const pdfHeightPt = viewport.height;
      const rotation = page.rotation;
      setPdfDimensionsPt({ width: pdfWidthPt, height: pdfHeightPt, rotation });

      const MAX_PREVIEW_WIDTH = 800;
      const aspectRatio = pdfWidthPt / pdfHeightPt;
      const previewWidth = Math.min(pdfWidthPt, MAX_PREVIEW_WIDTH);
      const previewHeight = previewWidth / aspectRatio;

      const canvas = certificateRef.current;
      const context = canvas.getContext("2d");

      canvas.width = previewWidth * window.devicePixelRatio;
      canvas.height = previewHeight * window.devicePixelRatio;
      canvas.style.width = `${previewWidth}px`;
      canvas.style.height = `${previewHeight}px`;
      context.scale(window.devicePixelRatio, window.devicePixelRatio);

      const renderViewport = page.getViewport({
        scale: previewWidth / pdfWidthPt,
      });
      const renderContext = {
        canvasContext: context,
        viewport: renderViewport,
      };

      await page.render(renderContext).promise;
      setCertificateDimensions({
        width: previewWidth,
        height: previewHeight,
      });
    } catch (error) {
      console.error("Error rendering PDF:", error);
    }
  };

  const handleMouseDown = (e, target) => {
    e.preventDefault();
    setIsDragging(true);
    setShowResizeDots(true); 
    setDragTarget(target);
    const rect = certificateRef.current.getBoundingClientRect();
    const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
    const scaleY = pdfDimensionsPt.height / certificateDimensions.height;

    if (target === "qr") {
      setActiveTab("QR");
      const pixelX = (qrPosition.x / 100) * certificateDimensions.width;
      const pixelY = (qrPosition.y / 100) * certificateDimensions.height;
      setDragStart({
        x: e.clientX - rect.left - pixelX,
        y: e.clientY - rect.top - pixelY,
      });
    } else {
      setActiveTab("verification");
      setDragStart({
        x: e.clientX - rect.left - verifierCoordsPt.x / scaleX,
        y: e.clientY - rect.top - verifierCoordsPt.y / scaleY,
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !certificateRef.current) return;

    e.preventDefault();
    const rect = certificateRef.current.getBoundingClientRect();
    const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
    const scaleY = pdfDimensionsPt.height / certificateDimensions.height;

    if (dragTarget === "qr") {
      const newPixelX = e.clientX - rect.left - dragStart.x;
      const newPixelY = e.clientY - rect.top - dragStart.y;

      const maxX = certificateDimensions.width - qrSize.width;
      const maxY = certificateDimensions.height - qrSize.height;

      const constrainedPixelX = Math.max(0, Math.min(maxX, newPixelX));
      const constrainedPixelY = Math.max(0, Math.min(maxY, newPixelY));

      setQrCoordsPt((prev) => ({
        ...prev,
       x: Math.round(constrainedPixelX * scaleX),
       y: Math.round(constrainedPixelY * scaleY),
      }));
      setQrPosition({
        x: (constrainedPixelX / certificateDimensions.width) * 100,
        y: (constrainedPixelY / certificateDimensions.height) * 100,
      });
    } else {
      const newPixelX = e.clientX - rect.left - dragStart.x;
      const newPixelY = e.clientY - rect.top - dragStart.y;
      const maxX = certificateDimensions.width - verifierCoordsPt.width / scaleX;
      const maxY = certificateDimensions.height - verifierCoordsPt.height / scaleY;

      const constrainedPixelX = Math.max(0, Math.min(maxX, newPixelX));
      const constrainedPixelY = Math.max(0, Math.min(maxY, newPixelY));

      setVerifierCoordsPt((prev) => ({
        ...prev,
         x: Math.round(constrainedPixelX * scaleX),
         y: Math.round(constrainedPixelY * scaleY),
      }));
    }
  }, [isDragging, certificateRef, dragTarget, dragStart, certificateDimensions, pdfDimensionsPt, qrSize, setQrPosition, verifierCoordsPt]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragTarget(null);
    setShowResizeDots(false); 
  }, []);

  const handleResizeMouseDown = (e, handle, target) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    setShowResizeDots(true); 
    setResizeHandle(handle);
    setDragTarget(target);

    const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
    const scaleY = pdfDimensionsPt.height / certificateDimensions.height;

    if (target === "qr") {
      setActiveTab("QR");
      const pixelX = (qrPosition.x / 100) * certificateDimensions.width;
      const pixelY = (qrPosition.y / 100) * certificateDimensions.height;
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: qrSize.width,
        height: qrSize.height,
        elementX: pixelX,
        elementY: pixelY,
      });
    } else {
      setActiveTab("verification");
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        fontSize: verifierCoordsPt.fontSize,
        width: verifierCoordsPt.width,
        height: verifierCoordsPt.height,
        elementX: verifierCoordsPt.x / scaleX,
        elementY: verifierCoordsPt.y / scaleY,
      });
    }
  };

  const handleResizeMouseMove = useCallback((e) => {
    if (!resizing || !resizeHandle) return;
    e.preventDefault();

    const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
    const scaleY = pdfDimensionsPt.height / certificateDimensions.height;

    if (dragTarget === "qr") {
      let newPixelX = resizeStart.elementX;
      let newPixelY = resizeStart.elementY;
      let newWidth = resizeStart.width;

      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;

      switch (resizeHandle) {
        case "se":
          newWidth = resizeStart.width + Math.max(deltaX, deltaY);
          break;
        case "sw":
          newWidth = resizeStart.width - deltaX;
          break;
        case "ne":
          newWidth = resizeStart.width + deltaX;
          break;
        case "nw":  
          newWidth = resizeStart.width - Math.max(deltaX, deltaY);
          break;
        default:
          break;
      }

      const snappedSize = Math.round(newWidth / 5) * 5;
      const finalSize = Math.max(20, snappedSize);

      switch (resizeHandle) {
        case "se":
          newPixelX = resizeStart.elementX;
          newPixelY = resizeStart.elementY;
          break;
        case "sw":
          newPixelX = resizeStart.elementX + (resizeStart.width - finalSize);
          newPixelY = resizeStart.elementY;
          break;
        case "ne":
          newPixelX = resizeStart.elementX;
          newPixelY = resizeStart.elementY + (resizeStart.height - finalSize);
          break;
        case "nw":
          newPixelX = resizeStart.elementX + (resizeStart.width - finalSize);
          newPixelY = resizeStart.elementY + (resizeStart.height - finalSize);
          break;
        default:
          break;
      }
      
      const maxX = certificateDimensions.width - finalSize;
      const maxY = certificateDimensions.height - finalSize;

      const constrainedPixelX = Math.max(0, Math.min(maxX, newPixelX));
      const constrainedPixelY = Math.max(0, Math.min(maxY, newPixelY));

      setQrSize({ width: finalSize, height: finalSize });
      setQrPosition({
        x: (constrainedPixelX / certificateDimensions.width) * 100,
        y: (constrainedPixelY / certificateDimensions.height) * 100,
      });
      setQrCoordsPt({
        x: Math.round(constrainedPixelX * scaleX),
        y: Math.round(constrainedPixelY * scaleY),
        width: Math.round(finalSize * scaleX),
        height: Math.round(finalSize * scaleY),
      });
    } else {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      let newFontSize = resizeStart.fontSize;
      let newWidthPt = resizeStart.width;
      let newHeightPt = resizeStart.height;
      let newPixelX = resizeStart.elementX;
      let newPixelY = resizeStart.elementY;

      if (resizeHandle.includes("s")) {
        newFontSize = Math.max(5, resizeStart.fontSize + deltaY * 0.2);
        newHeightPt = Math.max(10, resizeStart.height + deltaY * scaleY);
      } else if (resizeHandle.includes("n")) {
        newFontSize = Math.max(5, resizeStart.fontSize - deltaY * 0.2);
        newHeightPt = Math.max(10, resizeStart.height - deltaY * scaleY);
        newPixelY = resizeStart.elementY + deltaY;
      }

      if (resizeHandle.includes("e")) {
        newWidthPt = Math.max(50, resizeStart.width + deltaX * scaleX);
      } else if (resizeHandle.includes("w")) {
        newWidthPt = Math.max(50, resizeStart.width - deltaX * scaleX);
        newPixelX = resizeStart.elementX + deltaX;
      }

      if (e.shiftKey && newWidthPt > 0 && newHeightPt > 0) {
        const aspectRatio = resizeStart.width / resizeStart.height;
        newHeightPt = newWidthPt / aspectRatio;
      }

      newWidthPt = Math.min(newWidthPt, pdfDimensionsPt.width);
      newHeightPt = Math.min(newHeightPt, pdfDimensionsPt.height);

      newPixelX = Math.max(0, Math.min(certificateDimensions.width - newWidthPt / scaleX, newPixelX));
      newPixelY = Math.max(0, Math.min(certificateDimensions.height - newHeightPt / scaleY, newPixelY));

      setVerifierCoordsPt((prev) => ({
        ...prev,
        fontSize: Math.round(newFontSize),
        width: Math.round(newWidthPt),
        height: Math.round(newHeightPt),
        x: Math.round(newPixelX * scaleX),
        y: Math.round(newPixelY * scaleY),
      }));
    }
  }, [resizing, resizeHandle, dragTarget, resizeStart, certificateDimensions, pdfDimensionsPt, setQrSize, setQrPosition]);

  const handleResizeMouseUp = useCallback(() => {
    setResizing(false);
    setResizeHandle(null);
    setDragTarget(null);
    setShowResizeDots(false); 
  }, []);

  useEffect(() => {
    if (isDragging || resizing) {
      document.addEventListener(
        "mousemove",
        isDragging ? handleMouseMove : handleResizeMouseMove
      );
      document.addEventListener(
        "mouseup",
        isDragging ? handleMouseUp : handleResizeMouseUp
      );
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousemove", handleResizeMouseMove);
      document.removeEventListener("mouseup", handleResizeMouseUp);
    };
  }, [isDragging, resizing, handleMouseMove, handleResizeMouseMove, handleMouseUp, handleResizeMouseUp]);

  useEffect(() => {
    const fetchQrData = async () => {
      if (
        !certificateDimensions.width ||
        !certificateDimensions.height ||
        !isOpen ||
        !pdfDimensionsPt.width ||
        !pdfDimensionsPt.height
      ) {
        return;
      }
      const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
      const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
      try {
        const response = await GET(
          `${EntityConstants.GET_QR_COORDINATES}/${issuer_credential_template_id}`
        );
        const data = await response.json();
        if (response.ok) {
          if (data.qr_coordinates) {
            const { x, y, width, height, is_active } = data.qr_coordinates;
            setQrCoordsPt({
            x: Math.round(x || 0),
            y: Math.round(y || 0),
            width: Math.round(width || 110),
            height: Math.round(height || 110),
            is_active: is_active !== undefined ? is_active : true,
          });
          const adjusted = adjustCoordinates(
            Math.round(x || 0),
            Math.round(y || 0),
            Math.round(width || 110),
            Math.round(height || 110),
            pdfDimensionsPt.width,
            pdfDimensionsPt.height,
            pdfDimensionsPt.rotation || 0
          );
          setQrSize({
            width: adjusted.w / scaleX,
            height: adjusted.h / scaleY,
          });
          setQrPosition({
            x: (adjusted.x / scaleX / certificateDimensions.width) * 100,
            y: (adjusted.y / scaleY / certificateDimensions.height) * 100,
          });
        }

        if (data.verifier_url_coordinates && data.verifier_url_coordinates.verifier_url) {
          const { x, y, font_size, width, height, verifier_url, is_active } = data.verifier_url_coordinates;
          setVerifierCoordsPt({
            x: Math.round(x || 0),
            y: Math.round(y || 0),
            fontSize: Math.round(font_size || 7),
            width: Math.round(width || 100),
            height: Math.round(height || 20),
            is_active: is_active !== undefined ? is_active : true,
          });
          setShowVerificationText(is_active !== undefined ? is_active : true);
          setEditableVerifierText(verifier_url); 
        } else {
          setVerifierCoordsPt((prev) => ({ ...prev, is_active: false }));
          setShowVerificationText(false);
          setEditableVerifierText(""); 
        }
        }
      } catch (error) {
        console.error("Error fetching QR data:", error);
      }
    };
      fetchQrData();
    }, [
      isOpen,
      certificateDimensions.width,
      certificateDimensions.height,
      pdfDimensionsPt.width,
      pdfDimensionsPt.height,
      pdfDimensionsPt.rotation,
      issuer_credential_template_id,
      setQrSize,
      setQrPosition,
    ]);

  const getPixelCoordinates = () => {
    if (!certificateDimensions.width || !certificateDimensions.height) {
      return { x: 0, y: 0 };
    }
    const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
    const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
    const adjusted = adjustCoordinates(
      qrCoordsPt.x,
      qrCoordsPt.y,
      qrCoordsPt.width,
      qrCoordsPt.height,
      pdfDimensionsPt.width,
      pdfDimensionsPt.height,
      pdfDimensionsPt.rotation || 0
    );
    return {
      x: adjusted.x / scaleX,
      y: adjusted.y / scaleY,
    };
  };

  const pixelCoords = getPixelCoordinates();

  useEffect(() => {
    if (isOpen) {
      loadPdf();
      const handleResize = () => {
        loadPdf();
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [isOpen, pdfUrl]);

  const handleSaveSettings = async () => {
    try {
      const payload = {
        qr_coordinates: {
          x: qrCoordsPt.x,
          y: qrCoordsPt.y,
          width: qrCoordsPt.width,
          height: qrCoordsPt.height,
          page_number: 1,
          is_active: qrCoordsPt.is_active,
        },
        verifier_url_coordinates: {
          x: verifierCoordsPt.x,
          y: verifierCoordsPt.y,
          width: verifierCoordsPt.width,
          height: verifierCoordsPt.height,
          font_size: verifierCoordsPt.fontSize,
          verifier_url: editableVerifierText,
          page_number: 1,
          is_active: showVerificationText,
        },
      };
      const response = await POST(
        `${EntityConstants.POST_QR_COORDINATES}/${issuer_credential_template_id}`,
        payload
      );

      if (response.ok) {
        Toast({
          type: "success",
          title: "Success",
          message: "Settings saved successfully",
        });
        onClose();
      }
    } catch (error) {
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to save settings",
      });
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <div
                  className="bg-white rounded-xl max-w-7xl w-full h-[90vh] flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Certificate settings dialog"
                >
                  <div className="p-4 border-b">
                    <h3 id="qr-modal-title" className="text-lg font-semibold flex items-center gap-2">
                      <QrCode className="h-5 w-5" />
                      Edit QR Code and Verification Text
                    </h3>
                    <p id="qr-modal-description" className="text-sm text-gray-500">
                      Adjust the QR code and verification text position on the
                      certificate
                    </p>
                  </div>
                  <div className="flex-1 px-4 py-2 overflow-hidden">
                    <div className="flex gap-6 h-full">
                      {/* PDF Certificate Preview */}
                      <div className="flex-1 min-w-0 flex flex-col">
                        {" "}
                        <div className="mb-2 text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-md">
                          <span className="font-medium">
                            📄 PDF Certificate Preview
                          </span>{" "}
                          - Interactive positioning for QR code and verification
                          text
                        </div>
                        <div className="relative border-2 border-gray-200 rounded-lg bg-gray-100 select-none overflow-auto">
                          {" "}
                          <div
                            className="relative"
                            style={{
                              width: `${certificateDimensions.width}px`,
                              height: `${certificateDimensions.height}px`,
                            }}
                          >
                            <canvas
                              ref={certificateRef}
                              className="block border border-gray-300 bg-white shadow-sm"
                            />

                            {/* QR Code */}
                            <div
                              className={`absolute ${
                                dragTarget === "qr"
                                  ? "cursor-grabbing"
                                  : "cursor-move"
                              }`}
                              style={{
                                left: `${pixelCoords.x}px`,
                                top: `${pixelCoords.y}px`,
                                zIndex: 10,
                                width: `${qrSize.width}px`,
                                height: `${qrSize.height}px`,
                              }}
                              onMouseDown={(e) => handleMouseDown(e, "qr")}
                              onKeyDown={(e) => handleKeyDown(e, () => setActiveTab("QR"))}
                              onMouseEnter={() => setShowResizeDots(true)}
                              onMouseLeave={() => {
                                if (!isDragging && !resizing) {
                                  setShowResizeDots(false);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              aria-label="QR Code - drag to reposition or use arrow keys in control panel"
                            >
                              <img
                                src={qrCode}
                                alt="QR Code"
                                className="block w-full h-full p-0 m-0" 
                                style={{
                                  display: "block",
                                  margin: 0,
                                  padding: 0,
                                  border: "none",
                                }}
                              />

                              {/* Resize handles */}
                              {showResizeDots &&
                                ["nw", "ne", "sw", "se"].map((handle) => {
                                  const isNorth = handle.includes("n");
                                  const isWest = handle.includes("w");

                                  return (
                                    <div
                                      key={handle}
                                      className="absolute w-3 h-3 bg-blue-500 rounded-full pointer-events-auto"
                                      style={{
                                        [isNorth ? "top" : "bottom"]: "-4px",
                                        [isWest ? "left" : "right"]: "-4px",
                                        cursor: `${handle}-resize`,
                                        zIndex: 20,
                                      }}
                                      onMouseDown={(e) =>
                                        handleResizeMouseDown(e, handle, "qr")
                                      }
                                      role="slider"
                                      aria-valuenow={qrCoordsPt.width}
                                      aria-valuemin={20}
                                      aria-valuemax={500}
                                      tabIndex={0}
                                      aria-label={`Resize QR code from ${handle} corner`}
                                    />
                                  );
                                })}
                            </div>

                            {/* Verification Text */}
                            {showVerificationText && (
                              <div
                                className={`absolute bg-none bg-opacity-70 rounded text-left ${
                                  dragTarget === "text"
                                    ? "cursor-grabbing"
                                    : "cursor-move"
                                }`}
                                style={{
                                  left: `${verifierCoordsPt.x / (pdfDimensionsPt.width / certificateDimensions.width)}px`,
                                  top: `${verifierCoordsPt.y / (pdfDimensionsPt.height / certificateDimensions.height)}px`,
                                  zIndex: 10,
                                  fontSize: `${verifierCoordsPt.fontSize / (pdfDimensionsPt.width / certificateDimensions.width)}px`,
                                  width: `${verifierCoordsPt.width / (pdfDimensionsPt.width / certificateDimensions.width)}px`,
                                  height: `${verifierCoordsPt.height / (pdfDimensionsPt.height / certificateDimensions.height)}px`,
                                  border: "1px dashed #999",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  fontFamily: "Helvetica, sans-serif",
                                  paddingLeft: "4px",  
                                }}
                                onMouseDown={(e) => handleMouseDown(e, "text")}
                                onKeyDown={(e) => handleKeyDown(e, () => setActiveTab("verification"))}
                                role="button"
                                tabIndex={0}
                                aria-label="Verification text - drag to reposition or use arrow keys in control panel"
                              >
                                {editableVerifierText || ""} 
                                <div
                                  className="absolute w-3 h-3 bg-green-500 rounded-full pointer-events-auto"
                                  style={{
                                    bottom: "-4px",
                                    right: "-4px",
                                    cursor: "se-resize",
                                    zIndex: 20,
                                  }}
                                  onMouseDown={(e) =>
                                    handleResizeMouseDown(e, "se", "text")
                                  }
                                  role="slider"
                                  aria-valuenow={verifierCoordsPt.fontSize}
                                  aria-valuemin={5}
                                  aria-valuemax={50}
                                  tabIndex={0}
                                  aria-label="Resize verification text"
                                />
                              </div>
                            )}

                            {/* PDF Dimensions Overlay */}
                            <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                              PDF: {Math.round(pdfDimensionsPt.height)} ×{" "}
                              {Math.round(pdfDimensionsPt.width)}pt
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* QR Code Settings Panel */}
                      <div className="w-80 flex-shrink-0 bg-gray-50 rounded-lg p-4 overflow-y-auto">
                        <div className="flex w-full mb-4">
                          <div className="flex flex-col w-full items-center bg-gray-300 rounded-xl p-1">
                            <div className="flex space-x-2">
                              <button
                                className={`px-4 py-1 transition-all duration-200 ${
                                  activeTab === "QR"
                                    ? "bg-white text-black rounded-xl border-none focus:outline-none focus:ring-0"
                                    : "bg-gray-300 rounded-lg border-none focus:outline-none focus:ring-0"
                                }`}
                                onClick={() => setActiveTab("QR")}
                                onKeyDown={(e) => handleKeyDown(e, () => setActiveTab("QR"))}
                                role="tab"
                                aria-selected={activeTab === "QR"}
                                aria-controls="qr-settings-panel"
                              >
                                QR Settings
                              </button>
                              <button
                                className={`px-3 py-1 transition-all duration-200 ${
                                  activeTab === "verification"
                                    ? "bg-white text-black rounded-xl border-none focus:outline-none focus:ring-0"
                                    : "bg-gray-300 rounded-lg border-none focus:outline-none focus:ring-0"
                                }`}
                                onClick={() => setActiveTab("verification")}
                                onKeyDown={(e) => handleKeyDown(e, () => setActiveTab("verification"))}
                                role="tab"
                                aria-selected={activeTab === "verification"}
                                aria-controls="text-settings-panel"
                              >
                                Text Settings
                              </button>
                            </div>
                          </div>
                        </div>

                      {activeTab === "QR" && (
                        <div id="qr-settings-panel" role="tabpanel">
                        <div className="bg-white rounded-lg p-4 border mb-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Point Coordinates
                          </h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label htmlFor="qr-x-points" className="text-xs text-gray-500 block mb-1">
                                X Points
                              </label>
                              <input
                                id="qr-x-points"
                                type="number"
                                min="0"
                                max={pdfDimensionsPt.width}
                                value={qrCoordsPt.x}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const newX = value === '' ? '' : Number.parseInt(value, 10);
                                  if (isNaN(newX)) return;

                                  setQrCoordsPt((prev) => ({ ...prev, x: newX }));

                                  const numericValue = newX === '' ? 0 : newX;
                                  const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
                                  const pixelX = numericValue / scaleX;
                                  setQrPosition((prev) => ({
                                    ...prev,
                                    x: (pixelX / certificateDimensions.width) * 100,
                                  }));
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    setQrCoordsPt((prev) => ({ ...prev, x: 0 }));
                                  }
                                }}
                                className="w-full text-sm font-mono bg-gray-100 px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                            <div>
                              <label htmlFor="qr-y-points" className="text-xs text-gray-500 block mb-1">
                                Y Points
                              </label>
                              <input
                                id="qr-y-points"
                                type="number"
                                min="0"
                                max={pdfDimensionsPt.height}
                                value={qrCoordsPt.y}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const newY = value === '' ? '' : Number.parseInt(value, 10);
                                  if (isNaN(newY)) return;

                                  setQrCoordsPt((prev) => ({ ...prev, y: newY }));

                                  const numericValue = newY === '' ? 0 : newY;
                                  const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
                                  const pixelY = numericValue / scaleY;
                                  setQrPosition((prev) => ({
                                    ...prev,
                                    y: (pixelY / certificateDimensions.height) * 100,
                                  }));
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    setQrCoordsPt((prev) => ({ ...prev, y: 0 }));
                                  }
                                }}
                                className="w-full text-sm font-mono bg-gray-100 px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            PDF Size: {Math.round(pdfDimensionsPt.height)} ×{" "}
                            {Math.round(pdfDimensionsPt.width)}pt
                          </div>
                          <div className="mt-1 text-xs text-blue-600">
                            💡 These point coordinates will be used for QR
                            placement on the final PDF
                          </div>
                        </div>

                        <div className="bg-white rounded-lg p-4 border mb-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            QR Code Size (points)
                          </h4>
                          <div>
                            <label htmlFor="qr-size" className="text-xs text-gray-500 block mb-1">
                              Size (Height × Width)
                            </label>
                            <input
                              id="qr-size"
                              type="number"
                              min="20"
                              max="500"
                              value={qrCoordsPt.width || ""}
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                if (rawValue === "") {
                                  setQrCoordsPt((prev) => ({ ...prev, width: "", height: "" }));
                                  return;
                                }
                              const value = Number.parseInt(rawValue, 10) || 0;
                              setQrCoordsPt((prev) => ({
                                ...prev,
                                width: value,
                                height: value,
                              }));
                              const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
                              const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
                              setQrSize({
                                width: value / scaleX,
                                height: value / scaleY,
                              });
                            }}
                            onBlur={(e) => {
                              const value = Number.parseInt(e.target.value, 10);
                              const newSize = isNaN(value)
                                ? 20
                                : Math.max(20, Math.min(500, value));
                              setQrCoordsPt((prev) => ({
                                ...prev,
                                width: newSize,
                                height: newSize,
                                }));
                                const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
                                const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
                                setQrSize({
                                  width: newSize / scaleX,
                                  height: newSize / scaleY,
                              });
                            }}
                            className="w-28 text-sm font-mono bg-gray-100 px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200"
                            onClick={() => {
                                setQrCoordsPt((prev) => ({ ...prev, width: 110, height: 110 }));
                                const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
                                const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
                                setQrSize({ width: 110 / scaleX, height: 110 / scaleY });
                              }}
                            onKeyDown={(e) => handleKeyDown(e, () => {
                              setQrCoordsPt((prev) => ({ ...prev, width: 110, height: 110 }));
                              const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
                              const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
                              setQrSize({ width: 110 / scaleX, height: 110 / scaleY });
                            })}
                          >
                            Reset to Default (110pt)
                          </button>
                        </div>
                      </div>

                        <div className="bg-white rounded-lg p-4 border mb-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">
                            Position Control
                          </h4>
                          <div className="flex flex-col items-center">
                            {/* Up Button */}
                            <button
                              className="px-4 py-2 w-8 h-8 flex items-center justify-center mb-1 bg-gray-100 rounded-md hover:bg-gray-200"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const moveUp = () => {
                                  setQrCoordsPt((prev) => ({
                                    ...prev,
                                    y: Math.max(0, prev.y - 1),
                                  }));
                                  const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
                                  setQrPosition((prev) => ({
                                    ...prev,
                                    y: (Math.max(0, qrCoordsPt.y - 1) / scaleY / certificateDimensions.height) * 100,
                                  }));
                                };
                                moveUp();
                                const interval = setInterval(moveUp, 100);
                                const handleMouseUp = () => {
                                  clearInterval(interval);
                                  document.removeEventListener(
                                    "mouseup",
                                    handleMouseUp
                                  );
                                  document.removeEventListener(
                                    "mouseleave",
                                    handleMouseUp
                                  );
                                };
                                document.addEventListener(
                                  "mouseup",
                                  handleMouseUp
                                );
                                document.addEventListener(
                                  "mouseleave",
                                  handleMouseUp
                                );
                              }}
                              onKeyDown={(e) => handleKeyDown(e, () => {
                                setQrCoordsPt((prev) => ({
                                  ...prev,
                                  y: Math.max(0, prev.y - 1),
                                }));
                              })}
                              aria-label="Move QR code up"
                            >
                              ↑
                            </button>

                            <div className="flex items-center gap-1">
                              {/* Left Button */}
                              <button
                                className="px-4 py-2 w-8 h-8 flex items-center justify-center bg-gray-100 rounded-md hover:bg-gray-200"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  const moveLeft = () => {
                                    setQrCoordsPt((prev) => ({
                                      ...prev,
                                      x: Math.max(0, prev.x - 1),
                                    }));
                                    const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
                                    setQrPosition((prev) => ({
                                      ...prev,
                                      x: (Math.max(0, qrCoordsPt.x - 1) / scaleX / certificateDimensions.width) * 100,
                                    }));
                                  };
                                  moveLeft();
                                  const interval = setInterval(
                                    moveLeft,
                                    100
                                  );
                                  const handleMouseUp = () => {
                                    clearInterval(interval);
                                    document.removeEventListener(
                                      "mouseup",
                                      handleMouseUp
                                    );
                                    document.removeEventListener(
                                      "mouseleave",
                                      handleMouseUp
                                    );
                                  };
                                  document.addEventListener(
                                    "mouseup",
                                    handleMouseUp
                                  );
                                  document.addEventListener(
                                    "mouseleave",
                                    handleMouseUp
                                  );
                                }}
                                onKeyDown={(e) => handleKeyDown(e, () => {
                                  setQrCoordsPt((prev) => ({
                                    ...prev,
                                    x: Math.max(0, prev.x - 1),
                                  }));
                                })}
                                aria-label="Move QR code left"
                              >
                                ←
                              </button>

                              {/* Center/Reset Button */}
                              <button
                                className="px-4 py-2 w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300"
                                onClick={() => {
                                  const centerX = Math.round(pdfDimensionsPt.width / 2);
                                  const centerY = Math.round(pdfDimensionsPt.height / 2);
                                  setQrCoordsPt((prev) => ({
                                    ...prev,
                                    x: centerX,
                                    y: centerY,
                                  }));
                                  const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
                                  const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
                                  setQrPosition({
                                    x: (centerX / scaleX / certificateDimensions.width) * 100,
                                    y: (centerY / scaleY / certificateDimensions.height) * 100,
                                  });
                                }}
                                onKeyDown={(e) => handleKeyDown(e, () => {
                                  const centerX = Math.round(pdfDimensionsPt.width / 2);
                                  const centerY = Math.round(pdfDimensionsPt.height / 2);
                                  setQrCoordsPt((prev) => ({
                                    ...prev,
                                    x: centerX,
                                    y: centerY,
                                  }));
                                })}
                                title="Reset to center"
                                aria-label="Reset QR code to center position"
                              >
                                ⌂
                              </button>

                              {/* Right Button */}
                              <button
                                className="px-4 py-2 w-8 h-8 flex items-center justify-center bg-gray-100 rounded-md hover:bg-gray-200"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  const moveRight = () => {
                                    setQrCoordsPt((prev) => ({
                                      ...prev,
                                      x: Math.min(pdfDimensionsPt.width, prev.x + 1),
                                    }));
                                    const scaleX = pdfDimensionsPt.width / certificateDimensions.width;
                                    setQrPosition((prev) => ({
                                      ...prev,
                                      x: (Math.min(pdfDimensionsPt.width, qrCoordsPt.x + 1) / scaleX / certificateDimensions.width) * 100,
                                    }));
                                  };
                                  moveRight();
                                  const interval = setInterval(
                                    moveRight,
                                    100
                                  );
                                  const handleMouseUp = () => {
                                    clearInterval(interval);
                                    document.removeEventListener(
                                      "mouseup",
                                      handleMouseUp
                                    );
                                    document.removeEventListener(
                                      "mouseleave",
                                      handleMouseUp
                                    );
                                  };
                                  document.addEventListener(
                                    "mouseup",
                                    handleMouseUp
                                  );
                                  document.addEventListener(
                                    "mouseleave",
                                    handleMouseUp
                                  );
                                }}
                                onKeyDown={(e) => handleKeyDown(e, () => {
                                  setQrCoordsPt((prev) => ({
                                    ...prev,
                                    x: Math.min(pdfDimensionsPt.width, prev.x + 1),
                                  }));
                                })}
                                aria-label="Move QR code right"
                              >
                                →
                              </button>
                            </div>

                            {/* Down Button */}
                            <button
                              className="px-4 py-2 w-8 h-8 flex items-center justify-center mt-1 bg-gray-100 rounded-md hover:bg-gray-200"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const moveDown = () => {
                                  setQrCoordsPt((prev) => ({
                                    ...prev,
                                    y: Math.min(pdfDimensionsPt.height, prev.y + 1),
                                  }));
                                  const scaleY = pdfDimensionsPt.height / certificateDimensions.height;
                                  setQrPosition((prev) => ({
                                    ...prev,
                                    y: (Math.min(pdfDimensionsPt.height, qrCoordsPt.y + 1) / scaleY / certificateDimensions.height) * 100,
                                  }));
                                };
                                moveDown();
                                const interval = setInterval(moveDown, 100);
                                const handleMouseUp = () => {
                                  clearInterval(interval);
                                  document.removeEventListener(
                                    "mouseup",
                                    handleMouseUp
                                  );
                                  document.removeEventListener(
                                    "mouseleave",
                                    handleMouseUp
                                  );
                                };
                                document.addEventListener(
                                  "mouseup",
                                  handleMouseUp
                                );
                                document.addEventListener(
                                  "mouseleave",
                                  handleMouseUp
                                );
                              }}
                              onKeyDown={(e) => handleKeyDown(e, () => {
                                setQrCoordsPt((prev) => ({
                                  ...prev,
                                  y: Math.min(pdfDimensionsPt.height, prev.y + 1),
                                }));
                              })}
                              aria-label="Move QR code down"
                            >
                              ↓
                            </button>
                          </div>

                          <div className="mt-2 text-center">
                            <p className="text-xs text-gray-500">
                              Click and hold to move
                            </p>
                            <p className="text-xs text-gray-400">
                              Center button resets position
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "verification" && (
                      <div id="text-settings-panel" role="tabpanel">
                        {/* Verification Text */}
                        <div className="bg-white rounded-lg p-4 border mb-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-700">
                              Verification Text
                            </h4>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={showVerificationText}
                                onChange={() => {
                                  setShowVerificationText(!showVerificationText);
                                  setVerifierCoordsPt((prev) => ({ ...prev, is_active: !showVerificationText }));
                                }}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              <span className="sr-only">Toggle verification text</span>
                            </label>
                          </div>

                          {showVerificationText && (
                            <>
                             <div className="mb-2">
                              <label htmlFor="verification-text" className="text-xs text-gray-500 block mb-1">
                                Verification Text Content
                              </label>
                              <textarea
                                id="verification-text"
                                value={editableVerifierText}
                                onChange={(e) => {
                                  setEditableVerifierText(e.target.value);
                                
                                  const charWidth = verifierCoordsPt.fontSize * 0.6;
                                  const textWidth = Math.max(
                                    50,
                                    Math.round(e.target.value.length * charWidth)
                                  );
                                  setVerifierCoordsPt((prev) => ({
                                    ...prev,
                                    width: textWidth,
                                  }));
                                }}
                                placeholder="Enter text here..."
                                className="w-full text-sm bg-white text-black appearance-none px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                rows={3}
                              />
                            </div>
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label htmlFor="text-x-points" className="text-xs text-gray-500 block mb-1">
                                    X Points
                                  </label>
                                  <input
                                    id="text-x-points"
                                    type="number"
                                    min="0"
                                    max={pdfDimensionsPt.width}
                                    value={verifierCoordsPt.x}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const newX = value === '' ? '' : Number.parseInt(value, 10);
                                      if (isNaN(newX)) return;
                                      setVerifierCoordsPt((prev) => ({
                                        ...prev,
                                        x: newX,
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      if (e.target.value === '') {
                                        setVerifierCoordsPt((prev) => ({ ...prev, x: 0 }));
                                      }
                                    }}
                                    className="w-full text-sm font-mono bg-gray-100 px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  />
                                </div>
                                <div>
                                  <label htmlFor="text-y-points" className="text-xs text-gray-500 block mb-1">
                                    Y Points
                                  </label>
                                  <input
                                    id="text-y-points"
                                    type="number"
                                    min="0"
                                    max={pdfDimensionsPt.height}
                                    value={verifierCoordsPt.y}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const newY = value === '' ? '' : Number.parseInt(value, 10);
                                      if (isNaN(newY)) return;
                                      setVerifierCoordsPt((prev) => ({
                                        ...prev,
                                        y: newY,
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      if (e.target.value === '') {
                                        setVerifierCoordsPt((prev) => ({ ...prev, y: 0 }));
                                      }
                                    }}
                                    className="w-full text-sm font-mono bg-gray-100 px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  />
                                </div>
                              </div>

                              <div className="mb-3">
                                <label htmlFor="text-font-size" className="text-xs text-gray-500 block mb-1">
                                  Font Size (pt)
                                </label>
                                <input
                                  id="text-font-size"
                                  type="number"
                                  min="0"
                                  value={verifierCoordsPt.fontSize}
                                  onChange={(e) => {
                                    const value = Number.parseInt(e.target.value, 10);
                                    const newSize = isNaN(value)
                                      ? 0
                                      : Math.max(0, value);
                                      const charWidth = newSize * 0.6;
                                      const textWidth = Math.max(
                                        50,
                                        Math.round((editableVerifierText.length || 20) * charWidth)
                                      );
                                      const textHeight = Math.max(10, Math.round(newSize * 1.2));
                                      setVerifierCoordsPt((prev) => ({
                                        ...prev,
                                        fontSize: newSize,
                                        width: textWidth,
                                        height: textHeight,
                                    }));
                                  }}
                                  className="w-full text-sm font-mono bg-gray-100 px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label htmlFor="text-height" className="text-xs text-gray-500 block mb-1">
                                    Height (pt)
                                  </label>
                                  <input
                                    id="text-height"
                                    type="number"
                                    min="0"
                                    max="500"
                                    value={verifierCoordsPt.height}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const newHeight = value === '' ? '' : Number.parseInt(value, 10);
                                      if (isNaN(newHeight)) return;
                                      setVerifierCoordsPt((prev) => ({
                                        ...prev,
                                        height: newHeight,
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      if (e.target.value === '') {
                                        setVerifierCoordsPt((prev) => ({ ...prev, height: 20 }));
                                      }
                                    }}
                                    className="w-full text-sm font-mono bg-gray-100 px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  />
                                </div>
                                <div>
                                  <label htmlFor="text-width" className="text-xs text-gray-500 block mb-1">
                                    Width (pt)
                                  </label>
                                  <input
                                    id="text-width"
                                    type="number"
                                    min="0"
                                    max="500"
                                    value={verifierCoordsPt.width}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const newWidth = value === '' ? '' : Number.parseInt(value, 10);
                                      if (isNaN(newWidth)) return;
                                      setVerifierCoordsPt((prev) => ({
                                        ...prev,
                                        width: newWidth,
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      if (e.target.value === '') {
                                        setVerifierCoordsPt((prev) => ({ ...prev, width: 100 }));
                                      }
                                    }}
                                    className="w-full text-sm font-mono bg-gray-100 px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {showVerificationText && (
                          <div className="bg-white rounded-lg p-4 border mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">
                              Position Control
                            </h4>
                            <div className="flex flex-col items-center">
                              {/* Up Button */}
                              <button
                                className="px-4 py-2 w-8 h-8 flex items-center justify-center mb-1 bg-gray-100 rounded-md hover:bg-gray-200"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  const moveUp = () => {
                                    setVerifierCoordsPt((prev) => ({
                                      ...prev,
                                      y: Math.max(0, prev.y - 1),
                                    }));
                                  };
                                  moveUp();
                                  const interval = setInterval(moveUp, 100);
                                  const handleMouseUp = () => {
                                    clearInterval(interval);
                                    document.removeEventListener(
                                      "mouseup",
                                      handleMouseUp
                                    );
                                    document.removeEventListener(
                                      "mouseleave",
                                      handleMouseUp
                                    );
                                  };
                                  document.addEventListener(
                                    "mouseup",
                                    handleMouseUp
                                  );
                                  document.addEventListener(
                                    "mouseleave",
                                    handleMouseUp
                                  );
                                }}
                                onKeyDown={(e) => handleKeyDown(e, () => {
                                  setVerifierCoordsPt((prev) => ({
                                    ...prev,
                                    y: Math.max(0, prev.y - 1),
                                  }));
                                })}
                                aria-label="Move verification text up"
                              >
                                ↑
                              </button>

                              <div className="flex items-center gap-1">
                                {/* Left Button */}
                                <button
                                  className="px-4 py-2 w-8 h-8 flex items-center justify-center bg-gray-100 rounded-md hover:bg-gray-200"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    const moveLeft = () => {
                                      setVerifierCoordsPt((prev) => ({
                                        ...prev,
                                        x: Math.max(0, prev.x - 1),
                                      }));
                                    };
                                    moveLeft();
                                    const interval = setInterval(
                                      moveLeft,
                                      100
                                    );
                                    const handleMouseUp = () => {
                                      clearInterval(interval);
                                      document.removeEventListener(
                                        "mouseup",
                                        handleMouseUp
                                      );
                                      document.removeEventListener(
                                        "mouseleave",
                                        handleMouseUp
                                      );
                                    };
                                    document.addEventListener(
                                      "mouseup",
                                      handleMouseUp
                                    );
                                    document.addEventListener(
                                      "mouseleave",
                                      handleMouseUp
                                    );
                                  }}
                                  onKeyDown={(e) => handleKeyDown(e, () => {
                                    setVerifierCoordsPt((prev) => ({
                                      ...prev,
                                      x: Math.max(0, prev.x - 1),
                                    }));
                                  })}
                                  aria-label="Move verification text left"
                                >
                                  ←
                                </button>

                                {/* Center/Reset Button */}
                                <button
                                  className="px-4 py-2 w-8 h-8 flex items-center justify-center bg-gray-200 rounded-md hover:bg-gray-300"
                                  onClick={() => {
                                    setVerifierCoordsPt((prev) => ({
                                      ...prev,
                                       x: Math.round(pdfDimensionsPt.width / 2),
                                       y: Math.round(pdfDimensionsPt.height / 2),
                                    }));
                                  }}
                                  onKeyDown={(e) => handleKeyDown(e, () => {
                                    setVerifierCoordsPt((prev) => ({
                                      ...prev,
                                      x: Math.round(pdfDimensionsPt.width / 2),
                                      y: Math.round(pdfDimensionsPt.height / 2),
                                    }));
                                  })}
                                  title="Reset to center"
                                  aria-label="Reset verification text to center position"
                                >
                                  ⌂
                                </button>

                                {/* Right Button */}
                                <button
                                  className="px-4 py-2 w-8 h-8 flex items-center justify-center bg-gray-100 rounded-md hover:bg-gray-200"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    const moveRight = () => {
                                      setVerifierCoordsPt((prev) => ({
                                        ...prev,
                                        x: Math.min(
                                          pdfDimensionsPt.width,
                                          prev.x + 1
                                        ),
                                      }));
                                    };
                                    moveRight();
                                    const interval = setInterval(
                                      moveRight,
                                      100
                                    );
                                    const handleMouseUp = () => {
                                      clearInterval(interval);
                                      document.removeEventListener(
                                        "mouseup",
                                        handleMouseUp
                                      );
                                      document.removeEventListener(
                                        "mouseleave",
                                        handleMouseUp
                                      );
                                    };
                                    document.addEventListener(
                                      "mouseup",
                                      handleMouseUp
                                    );
                                    document.addEventListener(
                                      "mouseleave",
                                      handleMouseUp
                                    );
                                  }}
                                  onKeyDown={(e) => handleKeyDown(e, () => {
                                    setVerifierCoordsPt((prev) => ({
                                      ...prev,
                                      x: Math.min(pdfDimensionsPt.width, prev.x + 1),
                                    }));
                                  })}
                                  aria-label="Move verification text right"
                                >
                                  →
                                </button>
                              </div>

                              {/* Down Button */}
                              <button
                                className="px-4 py-2 w-8 h-8 flex items-center justify-center mt-1 bg-gray-100 rounded-md hover:bg-gray-200"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  const moveDown = () => {
                                    setVerifierCoordsPt((prev) => ({
                                      ...prev,
                                      y: Math.min(
                                        pdfDimensionsPt.height,
                                        prev.y + 1
                                      ),
                                    }));
                                  };
                                  moveDown();
                                  const interval = setInterval(
                                    moveDown,
                                    100
                                  );
                                  const handleMouseUp = () => {
                                    clearInterval(interval);
                                    document.removeEventListener(
                                      "mouseup",
                                      handleMouseUp
                                    );
                                    document.removeEventListener(
                                      "mouseleave",
                                      handleMouseUp
                                    );
                                  };
                                  document.addEventListener(
                                    "mouseup",
                                    handleMouseUp
                                  );
                                  document.addEventListener(
                                    "mouseleave",
                                    handleMouseUp
                                  );
                                }}
                                onKeyDown={(e) => handleKeyDown(e, () => {
                                  setVerifierCoordsPt((prev) => ({
                                    ...prev,
                                    y: Math.min(pdfDimensionsPt.height, prev.y + 1),
                                  }));
                                })}
                                aria-label="Move verification text down"
                              >
                                ↓
                              </button>
                            </div>

                            <div className="mt-2 text-center">
                              <p className="text-xs text-gray-500">
                                Click and hold to move
                              </p>
                              <p className="text-xs text-gray-400">
                                Center button resets position
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                        {/* Instructions */}
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <h4 className="text-sm font-medium text-blue-800 mb-1">
                            PDF + QR Instructions
                          </h4>
                          <p className="text-xs text-blue-600">
                            Position the QR code and verification text on your
                            PDF certificate preview. The coordinates will be
                            used to embed them into the final PDF.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-2 border-t flex justify-end gap-2">
                    <button
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                      onClick={onClose}
                    >
                      Close
                    </button>
                    <button
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      onClick={handleSaveSettings}
                    >
                      Save Settings
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default QrEditModal;